import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, Wand2, Check, History, RotateCcw, ArrowRight, X, Code2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { PageTitle, Card, GoldButton, GhostButton, inputCls } from "@/admin/ui";

const EXAMPLES = [
  "Altere o tema do site para preto e dourado mais intenso",
  "Deixe o site com estilo premium e sofisticado",
  "Troque o subtítulo por uma versão mais profissional",
  "Adicione o link do Telegram e ajuste o copyright para 2026",
  "Otimize o site para celular",
  "Melhore os textos da página inicial para aumentar a conversão",
];

const LABELS = {
  site_name: "Nome do site", tagline: "Subtítulo", logo: "Logo", favicon: "Favicon",
  instagram: "Instagram", telegram: "Telegram", email: "E-mail",
  color_primary: "Cor primária", color_secondary: "Cor secundária", color_accent: "Cor destaque",
  font_heading: "Fonte títulos", font_body: "Fonte texto", seo_title: "SEO título",
  seo_description: "SEO descrição", google_analytics: "Google Analytics", copyright: "Copyright",
  title: "SEO título", description: "SEO descrição", keywords: "Palavras-chave",
  og_image: "Imagem OG", canonical: "Canônica", robots: "Robots", google_verification: "Verificação Google",
};

export const AiAssistant = () => {
  const [command, setCommand] = useState("");
  const [loading, setLoading] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [applying, setApplying] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = () => api.get("/admin/ai/history").then(({ data }) => setHistory(data));
  useEffect(() => { loadHistory(); }, []);

  const generate = async () => {
    if (!command.trim()) return;
    setLoading(true); setProposal(null);
    try {
      const { data } = await api.post("/admin/ai/assist", { command });
      setProposal(data);
      const n = Object.keys(data.changes?.settings || {}).length + Object.keys(data.changes?.seo || {}).length + (data.changes?.custom_css ? 1 : 0);
      if (n === 0) toast.info("A IA não propôs alterações aplicáveis. Veja as observações.");
      else toast.success("Prévia gerada — revise e aplique");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao gerar sugestão");
    } finally { setLoading(false); }
  };

  const apply = async () => {
    setApplying(true);
    try {
      await api.post("/admin/ai/apply", { command, summary: proposal.summary, changes: proposal.changes });
      toast.success("Alterações aplicadas ao site!");
      setProposal(null); setCommand(""); loadHistory();
    } catch { toast.error("Erro ao aplicar"); } finally { setApplying(false); }
  };

  const restore = async (h) => {
    if (!window.confirm("Restaurar o site para o estado ANTES desta alteração?")) return;
    await api.post(`/admin/ai/restore/${h.id}`);
    toast.success("Versão restaurada"); loadHistory();
  };

  const diffRows = (obj, current, kind) => {
    if (!obj) return [];
    return Object.entries(obj).map(([k, v]) => ({
      key: k, label: LABELS[k] || k,
      before: kind === "settings" ? current?.settings_atuais?.[k] : current?.seo_atual?.[k],
      after: v,
    }));
  };

  const rows = proposal ? [
    ...diffRows(proposal.changes?.settings, proposal.current, "settings"),
    ...diffRows(proposal.changes?.seo, proposal.current, "seo"),
  ] : [];

  return (
    <div data-testid="admin-ai">
      <PageTitle title={<span className="flex items-center gap-2"><Sparkles className="w-6 h-6 text-[#F5C542]" /> Assistente IA</span>}
        subtitle="Descreva em português o que deseja alterar no site — a IA gera, você aprova." />

      <div className="grid lg:grid-cols-[1.3fr_1fr] gap-6">
        <Card>
          <label className="text-xs uppercase tracking-wider text-white/45 font-medium">Digite o que deseja alterar no site</label>
          <textarea
            value={command}
            onChange={(e) => setCommand(e.target.value)}
            rows={4}
            placeholder="Ex.: Altere o tema para preto e dourado e deixe o subtítulo mais profissional..."
            className={inputCls + " mt-2 resize-y"}
            data-testid="ai-command-input"
          />
          <div className="flex flex-wrap gap-2 mt-3">
            {EXAMPLES.map((ex) => (
              <button key={ex} onClick={() => setCommand(ex)} className="text-[11px] px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-white/60 hover:text-[#F5C542] hover:border-[#F5C542]/40 transition-colors" data-testid="ai-example">
                {ex}
              </button>
            ))}
          </div>
          <div className="mt-5">
            <GoldButton onClick={generate} disabled={loading} data-testid="ai-generate">
              <Wand2 className="w-4 h-4 inline mr-1" /> {loading ? "Gerando prévia..." : "Gerar Prévia"}
            </GoldButton>
          </div>

          <AnimatePresence>
            {proposal && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6 pt-6 border-t border-white/[0.06]" data-testid="ai-preview">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs uppercase tracking-wider text-[#F5C542] font-display">Prévia das alterações</span>
                </div>
                <p className="text-sm text-white/70 mb-4">{proposal.summary}</p>

                {rows.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {rows.map((r) => (
                      <div key={r.key} className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                        <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">{r.label}</div>
                        <div className="flex items-center gap-2 text-sm flex-wrap">
                          <span className="text-white/40 line-through break-all">{String(r.before ?? "—")}</span>
                          <ArrowRight className="w-3.5 h-3.5 text-[#F5C542] shrink-0" />
                          <span className="text-white break-all">{String(r.after)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {proposal.changes?.custom_css && (
                  <div className="mb-4">
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/40 mb-1"><Code2 className="w-3.5 h-3.5" /> CSS a ser injetado</div>
                    <pre className="text-xs text-[#F5C542]/90 bg-black/40 border border-white/10 rounded-xl p-3 overflow-x-auto max-h-40">{proposal.changes.custom_css}</pre>
                  </div>
                )}

                {proposal.unsupported && (
                  <div className="flex items-start gap-2 p-3 rounded-xl bg-[#FFD84D]/10 border border-[#FFD84D]/25 text-[#FFD84D] text-xs mb-4">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {proposal.unsupported}
                  </div>
                )}

                {rows.length === 0 && !proposal.changes?.custom_css ? (
                  <p className="text-white/40 text-sm">Nenhuma alteração aplicável foi proposta.</p>
                ) : (
                  <div className="flex gap-3">
                    <GoldButton onClick={apply} disabled={applying} data-testid="ai-apply">
                      <Check className="w-4 h-4 inline mr-1" /> {applying ? "Aplicando..." : "Aplicar Alterações"}
                    </GoldButton>
                    <GhostButton onClick={() => setProposal(null)}><X className="w-4 h-4 inline mr-1" /> Descartar</GhostButton>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </Card>

        <Card>
          <h3 className="font-display font-bold text-white mb-4 flex items-center gap-2"><History className="w-4 h-4 text-[#F5C542]" /> Histórico de Alterações</h3>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {history.map((h) => (
              <div key={h.id} className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.05]" data-testid={`ai-history-${h.id}`}>
                <div className="text-sm text-white/85">{h.summary || h.command}</div>
                <div className="text-[11px] text-white/40 mt-1">{(h.applied || []).join(", ") || "sem alterações"} • {new Date(h.created_at).toLocaleString("pt-BR")}</div>
                <button onClick={() => restore(h)} className="mt-2 flex items-center gap-1 text-xs text-white/60 hover:text-[#F5C542]" data-testid={`ai-restore-${h.id}`}>
                  <RotateCcw className="w-3.5 h-3.5" /> Restaurar versão anterior
                </button>
              </div>
            ))}
            {history.length === 0 && <p className="text-white/40 text-sm">Nenhuma alteração ainda.</p>}
          </div>
        </Card>
      </div>
    </div>
  );
};
